// ── 미팅지도 마커 표시 설정 — 지도 마커와 설정 미리보기가 같이 쓰는 한 벌 (2026-10-24) ──
// 사용자: "마커 표시되는 정해진 규칙… 설정할 수 있게" — 조각(상호·미팅시간·카테고리) 켜기·순서·옵션,
//   카테고리마다 표시 글자(예: 제주 → 항)와 마커 색. 테넌트 전체 적용.
//   마커 색 = **카테고리 색뿐**(안 고르면 파랑) — 사용자: "외부직원은 필요없는거야 그렇게 적용되서는 안돼"(2026-10-24),
//   색 기준 칸(소속·피드백·한 가지 색)은 없앴다. 예전에 저장된 color·singleColor 는 읽을 때 버린다.
// 처음 값·허용 값은 서버 api/services/tm/meeting-marker-config.js 와 같게 둘 것.
import { FEEDBACK_TONES, normalizeFeedbackToneKey } from './tones';

export const MARKER_PART_LABELS = { name: '상호', time: '미팅시간', category: '카테고리' };
const PART_KEYS = Object.keys(MARKER_PART_LABELS);
const DEFAULT_PARTS = [
  { key: 'name', on: true, chars: 5 },
  { key: 'time', on: true, format: 'raw' },
  { key: 'category', on: true, mode: 'first' },
];
// 미팅시간 모양(2026-10-24 사용자 선택: 저장된 글자 그대로 / 숫자 14:00)
export const MARKER_TIME_FORMATS = [
  { key: 'raw', label: '저장된 글자 그대로' },
  { key: 'hhmm', label: '숫자 14:00' },
];
// 카테고리 색을 고르지 않았을 때
export const DEFAULT_MARKER_TONE = 'blue';

/** 처음 값 = 상호 5글자 + 미팅시간(저장된 글자) + 카테고리 앞글자, 색은 모두 파랑. */
export function defaultMarkerConfig() {
  return { parts: DEFAULT_PARTS.map((p) => ({ ...p })), categoryLetters: {}, categoryColors: {} };
}

/**
 * 미팅시간 글자. 'hhmm' 이면 읽을 수 있는 시간만 '14:00' 꼴로 — 뒤에 글자가 붙었거나(2시이후·15:00~16:00)
 * 못 읽으면(2~3시·시간조율) 저장된 글자 그대로. 오전·오후가 없는 1~7시는 오후로 본다(영업 시간, 사용자 확인 2026-10-24).
 */
export function formatMarkerTime(text, format) {
  const raw = String(text || '').trim();
  if (format !== 'hhmm' || !raw) return raw;
  const m = raw.match(/^(오전|오후)?\s*(\d{1,2})\s*(?::\s*(\d{1,2})|시\s*(?:(\d{1,2})\s*분)?)$/);
  if (!m) return raw;
  let h = Number(m[2]);
  const min = Number(m[3] ?? m[4] ?? 0);
  if (m[1] === '오후' && h < 12) h += 12;
  else if (m[1] === '오전' && h === 12) h = 0;
  else if (!m[1] && h >= 1 && h <= 7) h += 12;
  if (h > 23 || min > 59) return raw;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
}

// 카테고리 이름 → 값 표. pick 이 허용하지 않는 값은 버린다(서버 sanitizeCategoryMap 과 같다).
function categoryMap(raw, pick) {
  const out = {};
  const src = raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
  for (const [cat, value] of Object.entries(src)) {
    const c = String(cat || '').trim();
    const v = pick(value);
    if (c && v) out[c] = v;
  }
  return out;
}

/** 받은 값을 허용된 모양으로(서버 sanitizeMarkerConfig 와 같은 규칙). */
export function normalizeMarkerConfig(input) {
  const src = input && typeof input === 'object' && !Array.isArray(input) ? input : {};
  const seen = new Set();
  const parts = [];
  for (const p of Array.isArray(src.parts) ? src.parts : []) {
    const key = String(p?.key || '');
    if (!PART_KEYS.includes(key) || seen.has(key)) continue;
    seen.add(key);
    const out = { key, on: p.on !== false };
    if (key === 'name') { const n = Number(p.chars); out.chars = Number.isInteger(n) ? Math.min(10, Math.max(2, n)) : 5; }
    if (key === 'time') out.format = MARKER_TIME_FORMATS.some((f) => f.key === p.format) ? p.format : 'raw';
    if (key === 'category') out.mode = p.mode === 'full' ? 'full' : 'first';
    parts.push(out);
  }
  for (const d of DEFAULT_PARTS) if (!seen.has(d.key)) parts.push({ ...d });
  const toneKeys = new Set(FEEDBACK_TONES.map((t) => t.key));
  return {
    parts,
    categoryLetters: categoryMap(src.categoryLetters, (v) => [...String(v ?? '').trim()].slice(0, 3).join('')),
    categoryColors: categoryMap(src.categoryColors, (v) => (toneKeys.has(v) ? v : '')),
  };
}

/** 카테고리 표시 글자 — 지정한 글자가 있으면 그것, 없으면 앞 1글자 또는 이름 전체. 카테고리가 없으면 ''. */
export function categoryMarkerText(category, part, letters) {
  const cat = String(category || '').trim();
  if (!cat) return '';
  const custom = letters?.[cat];
  if (custom) return custom;
  return part?.mode === 'full' ? cat : ([...cat][0] || '');
}

/** 마커 글자 — 켜진 조각을 순서대로 띄어쓰기로 잇는다. 카테고리는 서버가 준 ticket_type_category. */
export function buildMarkerLabel(item, config) {
  const cfg = config || defaultMarkerConfig();
  const out = [];
  for (const p of cfg.parts) {
    if (!p.on) continue;
    if (p.key === 'name') out.push(String(item?.company_name || '').trim().slice(0, p.chars || 5) || '-');
    if (p.key === 'time') out.push(formatMarkerTime(item?.visit_time, p.format) || '-');
    if (p.key === 'category') {
      const t = categoryMarkerText(item?.ticket_type_category, p, cfg.categoryLetters);
      if (t) out.push(t);
    }
  }
  return out.join(' ') || '-';
}

/** 카테고리 색 키 — 고른 색, 없으면 파랑. */
export function categoryTone(category, config) {
  return normalizeFeedbackToneKey(config?.categoryColors?.[String(category || '').trim()], DEFAULT_MARKER_TONE);
}

/** 마커 색 — { bg, fg, selBg, textShadow }. 카테고리 색(피드백 옵션과 같은 7색, index.css --mm-tone-*). */
export function markerColors(item, config) {
  const key = categoryTone(item?.ticket_type_category, config);
  return {
    bg: `var(--mm-tone-${key}-solid-bg)`,
    fg: `var(--mm-tone-${key}-solid-text)`,
    selBg: `var(--mm-tone-${key}-solid-border)`,
    textShadow: '0 1px 1px rgba(0,0,0,0.25)',
  };
}
